import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/hooks/useI18n";
import { Rocket, Search } from "lucide-react";
import releaseNotes from "@/data/release-notes.json";

interface Release {
  version: string;
  date: string;
  notes: string[];
}

const formatDate = (date: string, language: string): string => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(language === "it" ? "it-IT" : "en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const ReleasesTab = () => {
  const { t, language } = useI18n();
  const [search, setSearch] = useState("");

  const releases = releaseNotes.releases as Release[];
  const current = releaseNotes.current;

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return releases;
    return releases.filter(
      (release) =>
        release.version.toLowerCase().includes(query) ||
        release.notes.some((note) => note.toLowerCase().includes(query))
    );
  }, [releases, search]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center gap-3 pb-3">
          <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-purple-50 text-purple-600 flex-shrink-0">
            <Rocket className="h-5 w-5" />
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <CardTitle className="text-base font-semibold">
              {t("adminDashboard.releases.title")}
            </CardTitle>
            <span className="text-xs text-gray-500 mt-0.5">
              {t("adminDashboard.releases.subtitle", { count: releases.length })}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("adminDashboard.releases.searchPlaceholder")}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card>
          <CardContent>
            <p className="text-sm text-gray-500 py-10 text-center">
              {t("adminDashboard.releases.empty")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filtered.map((release) => (
            <Card key={release.version}>
              <CardHeader className="flex flex-row items-center gap-3 pb-3">
                <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                  <CardTitle className="text-base font-semibold font-mono">
                    v{release.version}
                  </CardTitle>
                  {release.version === current && (
                    <Badge className="text-xs">
                      {t("adminDashboard.releases.currentBadge")}
                    </Badge>
                  )}
                  <span className="text-xs text-gray-500">
                    {formatDate(release.date, language)}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {release.notes.map((note, index) => (
                    <li
                      key={index}
                      className="text-sm text-gray-700 flex gap-2 leading-relaxed"
                    >
                      <span className="text-gray-400 flex-shrink-0">•</span>
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReleasesTab;
